import { useState, useEffect } from "react";
import { Download, X, Smartphone } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

export function PWAInstallPrompt() {
  const { t } = useLanguage();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    // Check dismissed
    const d = localStorage.getItem('trp_pwa_dismissed');
    if (d && Date.now() - parseInt(d) < 7 * 24 * 60 * 60 * 1000) return;

    // iOS detection
    const ua = navigator.userAgent;
    const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(ios);

    // Android/Chrome install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // For iOS, show after 5 seconds
    if (ios) {
      const timer = setTimeout(() => setShowBanner(true), 5000);
      return () => { clearTimeout(timer); window.removeEventListener('beforeinstallprompt', handler); };
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') setShowBanner(false);
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShowBanner(false);
    localStorage.setItem('trp_pwa_dismissed', Date.now().toString());
  };

  if (!showBanner || dismissed) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 bg-[#111] border border-[var(--color-border)] shadow-2xl shadow-black/60 z-50 animate-in slide-in-from-bottom-4 duration-300" data-testid="pwa-install-banner">
      <div className="p-4">
        <button onClick={handleDismiss} className="absolute top-3 right-3 text-[var(--color-text-muted)] hover:text-[var(--color-text)]" data-testid="pwa-dismiss">
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-[#a855f7] flex items-center justify-center shrink-0">
            <Smartphone className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-white font-bold text-sm">{t.pwa_title || "Install TopRankPulse"}</p>
            <p className="text-[var(--color-text-muted)] text-xs mt-0.5">{t.pwa_desc || "Add to home screen for quick access, offline support and app-like experience"}</p>
          </div>
        </div>
        {isIOS ? (
          <div className="mt-3 bg-[var(--color-surface)] p-3 text-xs text-[var(--color-text)] leading-relaxed">
            {t.pwa_ios || "Tap the Share button"} <span className="inline-block w-4 h-4 border border-zinc-500 rounded text-center text-[10px] align-middle mx-0.5">↑</span> {t.pwa_ios2 || "then \"Add to Home Screen\""}
          </div>
        ) : (
          <button
            onClick={handleInstall}
            className="mt-3 w-full py-2.5 bg-[#a855f7] text-white text-sm font-bold uppercase tracking-wider hover:bg-purple-600 transition-colors flex items-center justify-center gap-2"
            data-testid="pwa-install-btn"
          >
            <Download className="w-4 h-4" />
            {t.pwa_install || "Install App"}
          </button>
        )}
      </div>
    </div>
  );
}
